import {
  toIso,
  CalendarIcon,
  DocumentIcon,
  GlobeIcon,
  JournalIcon,
  listTrips,
  MapIcon,
  PeopleIcon,
  Popover,
  TicketIcon,
  WalletIcon,
  type TripSummary,
} from '@trippath/shared';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router';

import { BarBudget } from './BarBudget';
import { BarSearch } from './BarSearch';
import { LiveBar } from './LiveBar';
import { useTripAccess } from './readOnly';
import { TripTree } from '../components/family/TripTree';

/** The trip's sections, each by its mark and its name; by their marks alone when the names do
 * not fit. The variants are a widget on the plan's map, not a page of their own; history,
 * settings and sharing are in the menu. */
const sections = [
  ['', 'plan', MapIcon],
  ['/bookings', 'bookings', TicketIcon],
  ['/calendar', 'calendar', CalendarIcon],
  ['/group', 'group', PeopleIcon],
  ['/today', 'today', WalletIcon],
  ['/countries', 'countries', GlobeIcon],
  ['/docs', 'docs', DocumentIcon],
  ['/journal', 'journal', JournalIcon],
] as const;

/** A length CSS gives, in pixels: none said is none. */
const px = (value: string) => Number.parseFloat(value) || 0;

/** What a row of items takes, the row's gaps between them counted. */
function rowWidth(row: Element, items: Element[]): number {
  const gap = px(getComputedStyle(row).columnGap);
  const widths = items.map((item) => (item as HTMLElement).offsetWidth);
  return widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(items.length - 1, 0);
}

/** How the sections are said: by their names or their marks alone, on the bar's own line or
 * on a line of their own under it; the rest of the line said short to keep them on it. */
interface Fit {
  compact: boolean;
  tight: boolean;
  below: boolean;
}

/**
 * How the sections fit: on the bar's line by their names, else there by their marks alone, else
 * there with the rest of the line said short — the connection by its dot, the budget without its
 * word, the trip without its days — and only when even that does not do, on a line of their own
 * under it (on a phone). What the sections need either way, and what the rest of the line takes
 * in full or short, is measured while so shown and remembered after.
 */
function useFit(nav: HTMLElement | null): Fit {
  const [fit, setFit] = useState<Fit>({ compact: false, tight: false, below: false });
  const needed = useRef({ names: 0, marks: 0 });
  const rests = useRef({ full: 0, short: 0 });
  useLayoutEffect(() => {
    const bar = nav?.parentElement;
    if (!nav || !bar) return;
    const measure = () => {
      const kind = nav.classList.contains('bar-tabs-compact') ? 'marks' : 'names';
      needed.current[kind] = rowWidth(nav, [...nav.children]);
      const style = getComputedStyle(bar);
      const inside = bar.clientWidth - px(style.paddingLeft) - px(style.paddingRight);
      // A search lying over the line takes none of it.
      const rest = [...bar.children].filter(
        (item) => item !== nav && getComputedStyle(item).position !== 'absolute',
      );
      const said = nav.classList.contains('bar-tabs-tight') ? 'short' : 'full';
      rests.current[said] = rowWidth(bar, rest) + px(style.columnGap);
      const { names, marks } = needed.current;
      const room = (as: 'full' | 'short') => inside - rests.current[as];
      // What is not yet measured is tried on the line first.
      const next =
        names <= room('full')
          ? { compact: false, tight: false, below: false }
          : marks === 0 || marks <= room('full')
            ? { compact: true, tight: false, below: false }
            : rests.current.short === 0 || marks <= room('short')
              ? { compact: true, tight: true, below: false }
              : { compact: names > inside, tight: true, below: true };
      setFit((was) =>
        was.compact === next.compact && was.tight === next.tight && was.below === next.below
          ? was
          : next,
      );
    };
    measure();
    const watch = new ResizeObserver(measure);
    const watchAll = () => {
      watch.disconnect();
      for (const item of [bar, ...bar.children]) watch.observe(item);
    };
    watchAll();
    // The search opened or closed, the budget come: the rest of the line is another.
    const changes = new MutationObserver(() => {
      watchAll();
      measure();
    });
    changes.observe(bar, { childList: true });
    return () => {
      watch.disconnect();
      changes.disconnect();
    };
  }, [nav, fit]);
  return fit;
}

/**
 * The bar of a trip's pages (every mockup): which trip, a switch to another, its sections, the
 * search and the budget. The theme and the menu follow it, from the shell (`App`).
 */
export function TripNav({ trip }: { trip: string }) {
  const { t } = useTranslation();
  const [trips, setTrips] = useState<TripSummary[]>([]);
  const [open, setOpen] = useState(false);
  const [nav, setNav] = useState<HTMLElement | null>(null);
  const { compact, tight, below } = useFit(nav);

  useEffect(() => {
    let live = true;
    listTrips().then(
      (list) => {
        if (live) setTrips(list);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [trip]);

  const current = trips.find((summary) => summary.id === trip);
  // Said as the trip's pages hold it: taken out of the archive, no longer.
  const { archived } = useTripAccess();
  // The journal is a section once the trip has begun.
  const [today] = useState(() => toIso(new Date()));
  const shown = sections.filter(
    ([path]) => path !== '/journal' || (current !== undefined && current.start <= today),
  );
  // In full where the bar says it short.
  const said = current && `${current.name} · ${t('trips.days', { count: current.days })}`;
  const base = `/trips/${trip}`;

  return (
    <>
      <Popover
        open={open}
        onOpenChange={setOpen}
        label={t('tabs.trips')}
        trigger={(props) => (
          <button type="button" className="bar-trip" {...props} aria-label={said} title={said}>
            <span className="bar-trip-name">{current ? current.name : t('tabs.trips')}</span>
            {current && (
              <span className="bar-trip-days">
                · {t('trips.daysShort', { count: current.days })}
              </span>
            )}
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        )}
      >
        {/* An archived trip is out of the switch, but for the one open. */}
        <TripTree
          trips={trips.filter((s) => s.archivedAt === null || s.id === trip)}
          current={trip}
          to={(summary) => `/trips/${summary.id}`}
          onPicked={() => {
            setOpen(false);
          }}
        >
          <li className="trip-switch-actions">
            <Link
              to="/"
              onClick={() => {
                setOpen(false);
              }}
            >
              {t('tabs.allTrips')}
            </Link>
          </li>
        </TripTree>
      </Popover>
      {archived && (
        <span className="bar-archived" title={t('trips.archivedNote')}>
          {t('trips.readOnly')}
        </span>
      )}
      <nav
        ref={setNav}
        className={[
          'bar-tabs',
          compact && 'bar-tabs-compact',
          tight && 'bar-tabs-tight',
          below && 'bar-tabs-below',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={t('tabs.sections')}
      >
        {shown.map(([path, key, Icon]) => (
          // A section's own pages are its too: the mail page is the bookings'.
          <NavLink key={key} to={`${base}${path}`} end={path === ''} title={t(`tabs.${key}`)}>
            <Icon size={16} />
            <span className="bar-tab-name">{t(`tabs.${key}`)}</span>
          </NavLink>
        ))}
      </nav>
      <LiveBar />
      <BarSearch trip={trip} />
      {current && <BarBudget trip={trip} budget={current.budget} />}
    </>
  );
}
